/**
 * The absolute cycles an exercise is actually prescribed in — and therefore both the ORIGIN and the
 * COLUMN SET every projection is measured from.
 *
 * ── A lifespan is not a window, and the two words are the backend's ──────────────────────────
 * A WINDOW is the AUTHORED form: an opening cycle, a closing cycle, and the cycles excluded between
 * them. A LIFESPAN is that window RESOLVED against its host — "a span with holes punched in it".
 * `ExerciseDraft` carries the window (it is what the save submits); this module resolves the
 * lifespan (it is what the matrix renders). `cycleWindow.ts` still answers the window question and
 * still has callers; what it cannot answer is which cycles an exercise actually runs in.
 *
 * ── A LIFESPAN IS A LIST, NOT A SPAN, and that is the whole point of the shape below ─────────
 * `(startCycle, cycleCount)` cannot express "every week except the third", so a caller holding those
 * two numbers has to guess the projection offset — and `cycleIndex − startCycle` is wrong the moment
 * the lifespan has a hole: **progression counts EXPOSURES, not elapsed cycles**, so a cycle the
 * exercise sits out advances nothing. `progression-projection-parity.json` pins that against the
 * real backend. The offset is the cycle's INDEX IN THIS LIST, which is why the list — never its ends
 * — is what travels.
 *
 * ── The resolved list is READ, not computed ──────────────────────────────────────────────────
 * The response reports it on `ExerciseTemplateResponse.cycles`, captured here as
 * `ExerciseDraft.serverCycles`: the exercise's own window narrowed inside its DAY's lifespan and
 * clipped to the phase, ascending, holes and all. This app cannot arrive at that list on its own —
 * it would have to intersect two windows and re-implement the backend's resolution — and the last
 * time mobile tried, it ignored `retirementCycle` and `excludedCycles` and drew a column for every
 * cycle of the phase. That column was not cosmetic: a pin on it came back 400
 * `plan_override_on_sat_out_cycle`, leaving a plan that could not be saved at all.
 *
 * ── Ported from `move-with-me-frontend/.../plans/builder/model/cycleLifespan.ts` ──────────────
 * One rename: `cycles` off the wire is `serverCycles` here, because `PhaseDraft.cycles` already
 * means a COUNT in this app and the two would otherwise read alike.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import { phaseStartCycles } from './cycleWindow';
import type { ExerciseDraft, PhaseDraft } from './planDraft.types';

/** The absolute cycles a row is prescribed in — resolved, ascending, duplicate-free. MAY BE EMPTY. */
export interface CycleLifespan {
  /**
   * Every absolute cycle (1-based) the row runs in, ascending. Its INDEX is the progression offset,
   * so `cycles[0]` is the authored base column and a sat-out cycle is simply ABSENT rather than
   * present-and-skipped.
   *
   * An EMPTY list is a real state, not a defect to paper over: a row whose whole window is excluded
   * or retired away is prescribed nowhere, and the backend refuses to save it (`plan_window_empty`).
   * Fabricating a column here would hide that refusal behind a grid the trainer can type into.
   */
  readonly cycles: readonly number[];
}

/** The contiguous run `start .. start + count − 1` — the lifespan of a row that sits out nothing. */
export function contiguousCycles(startCycle: number, cycleCount: number): number[] {
  const start = Math.max(1, startCycle);
  return Array.from({ length: Math.max(0, cycleCount) }, (_, offset) => start + offset);
}

/** A lifespan's first cycle — its authored base column — or null when the row runs nowhere. */
export function baseCycleOf(lifespan: CycleLifespan): number | null {
  return lifespan.cycles[0] ?? null;
}

/**
 * Whether a progression rule has anywhere to LAND on this row.
 *
 * Every rule in the vocabulary states what changes from one exposure to the NEXT. A row prescribed
 * in exactly one cycle has no next exposure, so every one of those sentences is about a cycle that
 * does not exist — the trainer would be authoring a rule that can never fire. An EMPTY lifespan
 * answers no for the same reason and one more: the backend refuses to save the row at all.
 */
export function hasNextCycle(lifespan: CycleLifespan): boolean {
  return lifespan.cycles.length > 1;
}

/**
 * How many EXPOSURES into the lifespan `cycle` sits — the progression offset, counted in cycles the
 * row actually runs, never in calendar distance. Null when the row does not run in that cycle at
 * all, which is the answer that stops a caller projecting onto a cell the backend would refuse.
 */
export function exposureOffsetOf(lifespan: CycleLifespan, cycle: number): number | null {
  const index = lifespan.cycles.indexOf(cycle);
  return index < 0 ? null : index;
}

/**
 * Resolve an exercise's lifespan inside its phase.
 *
 * `serverCycles` is the authority for WHICH cycles it inhabits — it already carries the exercise's
 * retirement, its holes, and the narrowing inside its day. The phase's CURRENT length is the
 * authority for how far the phase reaches, and the two disagree the moment the trainer resizes a
 * phase in this session. So the resolved list is clipped to the phase's live span, and cycles the
 * phase has GAINED since the load are appended — a row running to the end of its phase keeps running
 * to the end of the longer phase, which is what growing a phase means. A gained cycle the exercise
 * sits out or has retired past is not appended: those two are the trainer's own statement, and the
 * only part of the lifespan this app holds in authored form.
 *
 * With no `serverCycles` at all — an exercise added in THIS session — the lifespan is the phase span
 * from the clamped activation, minus the same authored holes. The clamp matters because the draft's
 * phase boundaries are recomputed locally on every cycle-count edit, so a server-assigned activation
 * can fall outside the phase the exercise now sits in.
 *
 * NULL and EMPTY are different answers and stay so: an empty `serverCycles` is the server saying
 * "prescribed nowhere", while null means no server answer exists yet. Collapsing the two fabricates
 * a full phase of columns for a row the backend materialises nothing for.
 */
export function exerciseLifespan(
  exercise: Pick<
    ExerciseDraft,
    'serverCycles' | 'activationCycle' | 'retirementCycle' | 'excludedCycles'
  >,
  phaseStartCycle: number,
  phaseCycleCount: number,
): CycleLifespan {
  const phaseStart = Math.max(1, phaseStartCycle);
  const phaseEnd = phaseStart + Math.max(1, phaseCycleCount) - 1;
  const excluded = new Set(exercise.excludedCycles);
  const retirement = exercise.retirementCycle;
  const inhabits = (cycle: number): boolean =>
    !excluded.has(cycle) && (retirement === null || cycle <= retirement);

  const resolved = exercise.serverCycles;
  if (resolved === null) {
    const activation = exercise.activationCycle;
    const start =
      activation === null ? phaseStart : Math.min(Math.max(activation, phaseStart), phaseEnd);
    return { cycles: contiguousCycles(start, phaseEnd - start + 1).filter(inhabits) };
  }
  if (resolved.length === 0) return { cycles: [] };

  const ascending = [...new Set(resolved)].sort((a, b) => a - b);
  const loadedEnd = ascending[ascending.length - 1] ?? phaseStart;
  return {
    cycles: [
      ...ascending.filter((cycle) => cycle >= phaseStart && cycle <= phaseEnd),
      ...contiguousCycles(loadedEnd + 1, phaseEnd - loadedEnd).filter(inhabits),
    ],
  };
}

/**
 * Locate an exercise anywhere in the draft by its slot token and resolve its lifespan.
 *
 * Where each phase STARTS is `phaseStartCycles`' answer, not a second walk of the same list:
 * `findExerciseCycleWindow` accumulated the starts inline and so did this, and the two copies of
 * "phase 1 begins at cycle 1, every later phase after the previous one's cycles" are exactly the
 * kind of pair that drifts once a phase gains a length rule.
 */
export function findExerciseLifespan(
  phases: readonly PhaseDraft[],
  exerciseSlotToken: string,
): CycleLifespan | null {
  const starts = phaseStartCycles(phases);
  for (const [index, phase] of phases.entries()) {
    const exercise = phase.days
      .flatMap((day) => day.exercises)
      .find((candidate) => candidate.slotToken === exerciseSlotToken);
    if (exercise !== undefined) return exerciseLifespan(exercise, starts[index] ?? 1, phase.cycles);
  }
  return null;
}
